import { cva, type VariantProps } from 'class-variance-authority'
import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
  useId,
  useState,
} from 'react'

import { cn } from '../cn'

// Input + Textarea — the form base (design/components/input.html).
// The field shell (border, radius, focus ring, state colours) is shared so
// every other form control inherits identical behaviour. Focus ring is
// 3.5px and theme-aware via color-mix on the token; never use a field
// without a <label> (see FormField — controls live inside one).

/** cva field shell (border, radius, 3.5px theme-aware focus ring, state and
 *  disabled/read-only colours) shared by every form control so all inherit
 *  identical behaviour. Variants: state (default/error/success), inputSize. */
export const inputVariants = cva(
  [
    'block w-full font-sans bg-surface text-text border-[1.5px] rounded-md outline-none',
    'transition-[border-color,box-shadow] duration-quick ease-vysto',
    'placeholder:text-text-3',
    'focus-visible:ring-[3.5px]',
    // kill the @tailwindcss/forms base focus ring on mouse focus; keep ours on keyboard focus
    '[&:focus:not(:focus-visible)]:shadow-none',
    'disabled:bg-field disabled:text-text-3 disabled:border-line disabled:cursor-not-allowed',
    'read-only:bg-surface-2',
  ],
  {
    variants: {
      state: {
        default:
          'border-input-line focus-visible:border-brand-blue focus-visible:ring-[color-mix(in_srgb,var(--brand-blue)_15%,transparent)]',
        error:
          'border-sens focus-visible:border-sens focus-visible:ring-[color-mix(in_srgb,var(--sens)_15%,transparent)]',
        success:
          'border-ok focus-visible:border-ok focus-visible:ring-[color-mix(in_srgb,var(--ok)_15%,transparent)]',
      },
      inputSize: {
        sm: 'text-[11.5px] px-[9px] py-[6px]',
        md: 'text-[12.5px] px-[11px] py-[9px]',
      },
    },
    defaultVariants: { state: 'default', inputSize: 'md' },
  },
)

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  /** Decorative leading icon (search, currency). pointer-events disabled. */
  iconStart?: ReactNode
  /** Trailing slot. If interactive it MUST be a real control with aria-label. */
  iconEnd?: ReactNode
}

/** Text input on the shared field shell, with optional `iconStart` (decorative)
 *  and `iconEnd` slots. Never use without a <label> — normally composed inside
 *  FormField, which injects the id/aria wiring. */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, state, inputSize, iconStart, iconEnd, ...rest },
  ref,
) {
  const control = (
    <input
      ref={ref}
      className={cn(
        inputVariants({ state, inputSize }),
        iconStart && 'pl-8',
        iconEnd && 'pr-9',
        className,
      )}
      {...rest}
    />
  )

  if (!iconStart && !iconEnd) return control

  return (
    <span className="relative flex w-full items-center">
      {iconStart && (
        <span
          aria-hidden
          className="pointer-events-none absolute left-[11px] inline-flex text-text-3 [&_svg]:size-[14px]"
        >
          {iconStart}
        </span>
      )}
      {control}
      {iconEnd && (
        <span className="absolute right-[7px] inline-flex items-center text-text-3 [&_svg]:size-[14px]">
          {iconEnd}
        </span>
      )}
    </span>
  )
})
Input.displayName = 'Input'

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows'>,
    VariantProps<typeof inputVariants> {
  /** Grow to fit content instead of a fixed/resizable box. */
  autoGrow?: boolean
  /** Show a `len / maxLength` counter (requires maxLength). */
  showCount?: boolean
}

/** Multiline input on the shared field shell. `autoGrow` sizes to content and
 *  disables manual resize; `showCount` renders a `len / maxLength` counter
 *  that also tracks controlled values. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    className,
    state,
    inputSize,
    autoGrow,
    showCount,
    maxLength,
    defaultValue,
    value,
    onChange,
    ...rest
  },
  ref,
) {
  const [internalLen, setInternalLen] = useState(() => String(value ?? defaultValue ?? '').length)
  // When controlled, the count must follow the prop (not just local edits), so
  // it can't go stale after an external value change / reset.
  const len = value !== undefined ? String(value).length : internalLen
  const countId = useId()
  // Pass only the one the caller provided, never both.
  const controlled =
    value !== undefined ? { value } : defaultValue !== undefined ? { defaultValue } : {}

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    if (showCount && value === undefined) setInternalLen(e.target.value.length)
    if (autoGrow) {
      e.target.style.height = 'auto'
      e.target.style.height = `${e.target.scrollHeight}px`
    }
    onChange?.(e)
  }

  return (
    <span className="flex w-full flex-col gap-[5px]">
      <textarea
        ref={ref}
        maxLength={maxLength}
        {...controlled}
        onChange={handleChange}
        aria-describedby={showCount && maxLength != null ? countId : undefined}
        className={cn(
          inputVariants({ state, inputSize }),
          'min-h-[62px] leading-normal',
          autoGrow ? 'resize-none overflow-hidden' : 'resize-y',
          className,
        )}
        {...rest}
      />
      {showCount && maxLength != null && (
        <span id={countId} className="self-end text-[10px] tabular-nums text-text-3">
          {len} / {maxLength}
        </span>
      )}
    </span>
  )
})
Textarea.displayName = 'Textarea'
